// https://material-ui.com/components/tables/
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import {
  lighten,
  withStyles,
  makeStyles,
  useTheme
} from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FilterListIcon from "@material-ui/icons/FilterList";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";

const useStyles1 = makeStyles(theme => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5)
  }
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onChangePage } = props;

  const handleFirstPageButtonClick = event => {
    onChangePage(event, 0);
  };

  const handleBackButtonClick = event => {
    onChangePage(event, page - 1);
  };

  const handleNextButtonClick = event => {
    onChangePage(event, page + 1);
  };

  const handleLastPageButtonClick = event => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onChangePage: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired
};

const headCells = [
  {
    id: "StkShape",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "SHAPE",
  },
  {
    id: "STWeight",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "WEIGHT"
  },
  {
    id: "Colour.0.From",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "COLOUR"
  },			
  {
    id: "Clarity.0.From",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "CLARITY"
  },
  {
    id: "Cut.0.From",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "CUT"
  },
  {
    id: "Polish.0.From",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "POLISH"
  },
  {
    id: "Symmetry.0.From",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "SYMMETRY"
  },
  {
    id: "FLUOR",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "FLUOR"
  },					
  {
    id: "LTH_WTH_DTH",
    minWidth: 150,
    numeric: false,
    disablePadding: true,
    label: "LTH / WTH / DTH"
  },
  {
    id: "LAB",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "LAB"
  },
  {
    id: "PPC",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "PPC"
  },
  {
    id: "BR",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "BR"
  },
  {
    id: "PRICE",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "PRICE"
  },
  {
    id: "INFO",
    minWidth: 100,
    numeric: false,
    disablePadding: true,
    label: "INFO"
  },
  /* { id: "calories", numeric: true, disablePadding: false, label: "Calories" },
  {
    id: "carbs",
    numeric: true,
    disablePadding: false,
    label: "Carbs (g)",
    format: value => value.toFixed(2)
  },
  {
    id: "price",
    label: "Price",
    minWidth: 100,
    numeric: true,
    disablePadding: false,
    format: value => value.toLocaleString("en-US")
  } */
];

/* HEADER STYLE */
const StyledTableCell = withStyles(theme => ({
  head: {
    backgroundColor: "#c9e4e0",
    textTransform: "uppercase"
    /* 
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
    */
  },
  body: {
    fontSize: 14
  }
}))(TableCell);

const StyledTableRow = withStyles(theme => ({
  root: {
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover
    }
  }
}))(TableRow);

function EnhancedTableHead(props) {
  const {
    classes,
    order,
    orderBy,
    onRequestSort
  } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <StyledTableRow>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "default"}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ minWidth: headCell.minWidth }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </StyledTableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

const useToolbarStyles = makeStyles(theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1)
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85)
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        },
  title: {
    flex: "1 1 100%"
  }
}));

const EnhancedTableToolbar = props => {
  const classes = useToolbarStyles();
  const { diamondsData } = props;

  return (
    <Toolbar className={clsx(classes.root, {[classes.highlight]: false})}>
      <Typography
        className={classes.title}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        CURRENTLY SHOWING ({diamondsData.totalDocs}) UNIQUE DIAMONDS
      </Typography>
      <Tooltip title="Filter list">
        <IconButton aria-label="filter list">
          <FilterListIcon />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  diamondsData: PropTypes.array.isRequired
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2)
  },
  table: {
    minWidth: 750
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  }
}));

export default function EnhancedTable({diamondsData, fetchDiamondsList}) {
  const classes = useStyles();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [page, setPage] = useState(0);
  const rowsPerPageOptions = [10, 25, 50, 100, { value: -1, label: "All" }];
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [diamondDataDocs, setDiamondDataDocs] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    const fetchDiamondsListAsync = async () => {
      await fetchDiamondsList(page+1, rowsPerPage, order, orderBy);
    }
    fetchDiamondsListAsync();
  }, [fetchDiamondsList, page, rowsPerPage, order, orderBy]);

  useEffect(() => {
    if(diamondsData.docs && diamondsData.docs !== diamondDataDocs){
      setDiamondDataDocs(diamondsData.docs);

      setRows(diamondsData.docs.map(d => ({
        StkShape:           d.StkShape,
        STWeight:           d.STWeight, 
        'Colour.0.From':    d.Colour && d.Colour.filter(c => c.From).map(c => c.From).join(', '),
        'Clarity.0.From':   d.Clarity && d.Clarity.filter(c => c.From).map(c => c.From).join(', '),
        'Cut.0.From':       d.Cut && d.Cut.filter(c => c.From).map(c => c.From).join(', ') || '-',
        'Polish.0.From':    d.Polish && d.Polish.filter(c => c.From).map(c => c.From).join(', '),
        'Symmetry.0.From':  d.Symmetry && d.Symmetry.filter(c => c.From).map(c => c.From).join(', '),
        INFO:               '', 
      })))
    }
  }, [setDiamondDataDocs, diamondDataDocs, diamondsData.docs]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);

  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar diamondsData={diamondsData} />
        <TableContainer>
          <Table
            className={classes.table}
            size="small" /* other supported value is 'medium' */
            aria-labelledby="tableTitle"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="row"
                      tabIndex={-1}
                      key={row.name}
                    >
                      {headCells.map((column, index) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            padding={column.disablePadding ? "none" : "default"}
                            key={column.id}
                            align={
                              column.align ||
                              (column.numeric ? "right" : "left")
                            }
                          >
                            {column.format && typeof value === "number"
                              ? column.format(value)
                              : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 25 * emptyRows }}>
                  <TableCell colSpan={6}  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          colSpan={3}
          component="div"
          count={diamondsData.totalDocs}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { "aria-label": "rows per page" },
            native: false
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
        />
      </Paper>
    </div>
  );
}
